import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'

import { CORPORATE_KEY, corporateApi } from '@/api/corporate'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDateTime } from '@/lib/format'
import { integer, readSearch } from '@/lib/search-params'

export const Route = createFileRoute('/_app/corporate/journal')({
  validateSearch: (search: Record<string, unknown>) =>
    readSearch({ page: integer({ min: 1 }) }, search),
  component: JournalPage,
})

const PAGE_SIZE = 50

/**
 * Le journal du livre (lot 45) : à part du journal de l'application, il dit
 * qui a lu le registre de l'art. 18.1, exporté le livre, répondu à une demande
 * d'accès, adopté une résolution ou écrit un registre.
 */
function JournalPage() {
  const { page } = Route.useSearch()
  const navigate = Route.useNavigate()
  const current = page ?? 1
  const audit = useQuery({
    queryKey: [...CORPORATE_KEY, 'audit', current],
    queryFn: () => corporateApi.audit({ page: current, page_size: PAGE_SIZE }),
  })
  const pages = audit.data ? Math.max(1, Math.ceil(audit.data.total / PAGE_SIZE)) : 1
  return (
    <Frame className="space-y-3" data-testid="journal-livre">
      <h2 className="font-semibold">Journal du livre</h2>
      <p className="text-sm text-muted-foreground">
        Séparé du journal de l’application : lectures du registre de l’art. 18.1, exports, demandes
        d’accès, adoptions et écritures.
      </p>
      {audit.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : audit.isError ? (
        <FormError>Le journal n’a pas pu être lu.</FormError>
      ) : audit.data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Rien d’inscrit pour l’instant.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-1.5 pr-3 font-medium">Quand</th>
                <th className="py-1.5 pr-3 font-medium">Qui</th>
                <th className="py-1.5 pr-3 font-medium">Quoi</th>
                <th className="py-1.5 font-medium">Détail</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {audit.data.items.map((entry, index) => (
                <tr key={`${entry.at}-${String(index)}`} data-testid="entree-journal">
                  <td className="py-2 pr-3 whitespace-nowrap tabular-nums">
                    {formatDateTime(entry.at)}
                  </td>
                  <td className="py-2 pr-3">{entry.actor.name}</td>
                  <td className="py-2 pr-3">
                    {entry.action}
                    {entry.object && (
                      <span className="block text-xs text-muted-foreground">{entry.object}</span>
                    )}
                  </td>
                  <td className="py-2 text-muted-foreground">{entry.detail ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label="Page précédente"
            disabled={current <= 1}
            onClick={() => {
              void navigate({ search: { page: current - 1 } })
            }}
          >
            <ChevronLeftIcon />
          </Button>
          <span className="tabular-nums">
            {current} / {pages}
          </span>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label="Page suivante"
            disabled={current >= pages}
            onClick={() => {
              void navigate({ search: { page: current + 1 } })
            }}
          >
            <ChevronRightIcon />
          </Button>
        </div>
      )}
    </Frame>
  )
}
